import React, { useState, useEffect, useContext, useRef } from 'react'
import styled from 'styled-components'
import { GroupTypeBase, StylesConfig } from 'react-select'

import i18n from 'live/services/i18n'

import testReport from '../../api/testReport'
import { ReportContext } from '../../../src/app'
import getPreviewBody from '../../api/getPreviewBody'
import type { Recipient } from '../../common/types/form'
import LiveSpan from '../../Form/components/Content/LiveSpan'
import type { OptionType } from '../../common/types/utilities'
import { actionUpdateReport } from '../../actions/updateReports'
import { Header } from '../../common/components/CardEditor/header'
import ListAsync from '../../Form/components/LookupsList/ListAsync'

import { StyledCardEditor } from '../../common/components/CardEditor/index'
import BarSettingsReport from '../../Form/components/Header/BarSettingsReport'
import testReportWithContextRecipient from '../../api/testReportWithContextRecipient'
import FollowingGlobalSpanIcon from '../../Form/components/Content/emailType/FollowinGlobalSpanIcon'
import EmailInputRecipient, { StyledEmailInputRecipient } from '../../common/components/EmailInputRecipient'
import { CustomListAsyncStyles } from '../../Form/components/ReportContent/components/GalleryEditor/galleryEditor'
import { StyledGeneralSpanIcon } from '../../common/components/ContentGeneralSpan'

interface Props {
    match: {
        params: {
            uid: string
        }
    }
}

const CLASS_PREFIX = `generalInformation--recipientsGroups__recipientsSelect`

export default function Content({ match }: Props): JSX.Element {
    const { report, dispatch } = useContext(ReportContext)
    const recipients = report?.mailSettings?.recipients || []
    const [previewRecipient, setPreviewRecipient] = useState<Recipient>(recipients[0])
    const [timespan, setTimespan] = useState<string>()
    const [email, setEmail] = useState<string[]>([])
    const [previewHTML, setPreviewHTML] = useState<string>('')
    const [loadingPreview, setLoadingPreview] = useState<boolean>(false)
    const [hideNoPreview, setHideNoPreview] = useState<boolean>(false)
    const [stateSend, setStateSend] = useState<string>('-- | --')
    const refIframe = useRef()
    const MIN_HEIGHT_PREVIEW_IFRAME = 400
    const [heightIFrame, setHeightIFrame] = useState<number>(MIN_HEIGHT_PREVIEW_IFRAME)

    const [stateReport, setStateReport] = useState({
        loading: true,
        startLoading: false,
        msg: null
    })

    const uid = match?.params?.uid

    useEffect(() => {
        setPreviewRecipient(report?.mailSettings?.recipients[0])
    }, [report])

    useEffect(() => {
        const reportUid = report?.uid

        const checkUpdateReport = (): boolean => {
            if (stateReport.startLoading) {
                return false
            } else {
                return stateReport.loading
            }
        }

        if (checkUpdateReport()) {
            setStateReport({
                loading: true,
                msg: null,
                startLoading: true
            })

            if (reportUid) {
                dispatch({ report: undefined })
            }

            actionUpdateReport(match.params.uid)
                .then(report => dispatch({ report }))
                .then(() => setStateReport({ loading: false, msg: 'success', startLoading: false }))
                .catch((error: Error) => {
                    console.error(error)
                    setStateReport({ loading: false, msg: error.message, startLoading: false })
                })
        }
    }, [match, report, stateReport, stateReport.loading, uid, stateReport.startLoading, dispatch])

    if (stateReport.loading) return <div>loading...</div>
    if (stateReport.msg !== 'success') return <div>{stateReport.msg}</div>

    const getPreview = (): void => {
        setLoadingPreview(true)
        setHideNoPreview(true)
        setPreviewHTML('')
        getPreviewBody(uid, previewRecipient, timespan)
            .then(result => setPreviewHTML(result))
            .catch(error => console.error('error,', error))
            .finally(() => {
                setLoadingPreview(false)
            })
    }
    const printPreview = () => {
        // --heightIFrame - MIN_HEIGHT_PREVIEW_IFRAME + 100
        const printWindow = window.open('', '') // `height=500,width=800`
        printWindow.document.write(previewHTML)
        printWindow.document.title = report?.title || 'report'
        printWindow.document.close()
        printWindow.print()
    }

    return (
        <div key={uid}>
            <BarSettingsReport pageName="preview" uid={uid} />
            <CustomCardEditor style={{ padding: '16px' }}>
                <EmailInputRecipient
                    title={i18n('Send e-mail test to:')}
                    required={true}
                    variant={'primary'}
                    values={email}
                    editMode={true}
                    displayMsg={false}
                    showTotalEmail={false}
                    onChange={setEmail}
                    className="customPreviewEmailInput"
                />
                <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <SendTestButton
                        className="btn btn-primary"
                        disabled={!(email.length > 0)}
                        onClick={() => {
                            setStateSend('loading...')                      
                            testReportWithContextRecipient(
                                report.uid,
                                timespan,
                                [
                                    {
                                        ...previewRecipient,
                                        enabled: true
                                    }
                                ],
                                email
                            )
                                .then(({ status }) => setStateSend(status))
                                .catch(() => setStateSend('Error'))
                        }}
                    >
                        <label style={{ color: 'white' }}>
                            <i className="far fa-paper-plane" /> {i18n('Send test')}
                        </label>
                    </SendTestButton>
                    <div style={{ marginTop: '10px', display: 'inline', marginLeft: '9px' }}>
                        <span style={{ display: 'flex', gap: '8px' }}>
                            {i18n('Sending status')}: {stateSend !== '--|--' && <strong>{stateSend}</strong>}
                        </span>
                    </div>
                </div>
            </CustomCardEditor>

            <CustomReportPreview className={`cardEditor`}>
                <Header editMode={false} style={{ borderBottom: 'solid 1px #C8CCD4', height: '40px' }}>
                    <label style={{ textTransform: 'uppercase' }}>{i18n('Report preview')} </label>
                </Header>                
                <div className="previewGroupOptions">
                    <div className="selectRecipient">
                        <label>
                            {i18n('Select Recipient Group')}:<RequiredFiled>*</RequiredFiled>
                        </label>
                        <ListAsync<string[]>
                            placeholder={'Select the recipient group'}
                            getApi={function () {
                                return new Promise(resolve => {
                                    resolve(recipients.map(recipient => recipient.name))
                                })
                            }}
                            isClearable={true}
                            isMulti={false}
                            value={{ label: previewRecipient?.name, value: previewRecipient?.name }}
                            onChange={option => {
                                if (!option) {
                                    setPreviewRecipient(undefined)
                                } else {
                                    const firstOption = (option[0] || option) as OptionType
                                    const value = firstOption?.value
                                    const newRecipient = recipients.find(({ name }) => name === value)
                                    if (!newRecipient) {
                                        setPreviewRecipient(undefined)
                                    } else {
                                        setPreviewRecipient(newRecipient)
                                    }
                                }
                            }}
                            callback={names => names.map(name => ({ label: name, value: name }))}
                            disabled={false}
                            editMode={true}
                            styles={CustomPrintPreview}
                        />
                    </div>
                    <Timestamp>
                        <LiveSpan
                            span={timespan || ''}
                            setSpan={newSpan => {
                                setTimespan(newSpan)
                            }}
                            defaultSpan={null}
                            altClassName="liveSpanInput"
                            newTitle={<span>Timespan:</span>}
                        />
                    </Timestamp>         
                    <div className="previewButtons">
                        <PrintPreviewButton
                            className="btn btn-secondary"
                            onClick={() => printPreview()}
                            disabled={heightIFrame - MIN_HEIGHT_PREVIEW_IFRAME > 150 ? false : true}
                        >
                            <label>
                                <i className="far fa-print" />
                                {i18n('Print preview')}
                            </label>
                        </PrintPreviewButton>
                        <RunPreviewButton
                            className="btn btn-primary"
                            onClick={() => getPreview()}
                            disabled={previewRecipient?.uuid === undefined}
                        >
                            <label>{i18n('Run preview')}</label>
                        </RunPreviewButton>
                    </div>
                </div>
                <div
                    className={`cardEditor--content`}
                    style={{
                        height: MIN_HEIGHT_PREVIEW_IFRAME + 10,
                        position: 'relative',
                        overflow: `${!hideNoPreview ? 'hidden' : 'auto'}`
                    }}
                >
                    <NoPreview open={hideNoPreview}>
                        <i className="far fa-file-search" />
                        <span className="noPreview">No preview.</span>
                        <span className="noPreviewText">
                            You must select a recipient group and click on "Run preview” button to preview your report.
                        </span>
                    </NoPreview>
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            position: 'absolute',
                            display: loadingPreview ? 'flex' : 'none',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: '#EEEEEE'
                        }}
                    >
                        <i
                            style={{
                                fontSize: '40px',
                                color: '#a0cbe9'
                            }}
                            className="fas fa-spinner fa-pulse"
                        />
                    </div>

                    <iframe
                        onLoad={() => {
                            const element = refIframe.current as any
                            const content = element?.contentDocument as Element
                            if (content) {
                                const body = content.querySelector('body')
                                const { height } = body.getBoundingClientRect()
                                if (height + MIN_HEIGHT_PREVIEW_IFRAME !== heightIFrame) {
                                    setHeightIFrame(height + MIN_HEIGHT_PREVIEW_IFRAME)
                                }
                            }
                        }}
                        ref={refIframe}
                        style={{ width: '100%', height: heightIFrame, border: 'none' }}
                        srcDoc={previewHTML}
                    ></iframe>
                </div>
            </CustomReportPreview>
        </div>
    )
}

const NoPreview = styled.div<{ open: boolean }>`
    display: ${({ open }) => (!open ? 'flex' : 'none')};
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 500px;

    background: ${({ theme }) => theme['gray-100']};

    .noPreview {
        padding-top: 8px;
        color: ${({ theme }) => theme['gray-700']};
        text-align: center;
        font-size: 13px;
        font-style: ${({ theme }) => theme['font-semibold']};
        font-weight: 600;
        line-height: normal;
    }
    .noPreviewText {
        width: 210px;
        color: ${({ theme }) => theme['gray-600']};
        text-align: center;
        font-style: ${({ theme }) => theme['font-regular']};
        font-size: 13px;
        font-style: italic;
        font-weight: 400;
        line-height: normal;
    }

    i {
        font-size: 32px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        color: ${({ theme }) => theme['gray-500']};
    }
`

const RequiredFiled = styled.span`
    color: rgb(196, 53, 53);
    font-size: 13px;
    margin-left: 5px;
`

const Timestamp = styled.div`
    display: flex;
    max-height: 32px;
    width: 200px;
    padding: 0 6px;

    .liveSpanInput {
        width: 200px;
        padding-bottom: 8px;
        position: relative;
        top: 2px;
        label {
            margin-bottom: 0px;
        }
        .caret {
            display: none;
        }
        .span-dropdown {
            /* border: 2px solid red !important; */
            max-height: 38px;
            padding: 4px;
            max-width: 210px;
            background-color: white;
            border: 1px solid #c8ccd4;
            border-radius: 3px;
            /* margin-bottom: 5px; */
            margin: 0;
            &:focus-within {
                border: 2px solid #568af3;
            }
            .dropdown-toggle {
                width: 100% !important;
            }
            .dropdown-menu {
            }
        }
    }
`

const CustomReportPreview = styled(StyledCardEditor)`
    margin-top: 24x;
    width: 100%;
    align-items: center;

    .previewGroupOptions {
        margin: 0;
        display: grid;
        grid-template-columns: 1fr 4fr 1fr;
        flex-direction: row;
        justify-content: space-between;
        padding: 5px;
        height: 90px;
        padding: 16px;
        max-width: 100%;

        .previewButtons {
            display: flex;
            /* flex-direction: row; */
            align-items: flex-end;
            justify-content: flex-start;
            padding: 2px;
        }

        .selectRecipient {
            width: 300px;

            label {
                color: ${({ theme }) => theme['gray-800']};
                font-family: ${({ theme }) => theme['font-semibold']};
                font-size: 14px;
                font-style: normal;
                font-weight: 600;
                line-height: 16px; /* 114.286% */
                padding-bottom: 4px;
            }
        }

        .GESettingsSection {
            display: inline;
            /* flex: 1;
            min-width: 500px;
            width: 500px;
            padding: 10px 10px 0px 10px; */
            &__Block {
                /* margin-bottom: 30px; */
                width: 210px;
                height: 32px;
                label {
                    display: none;
                    margin-left: 4px;
                    font-style: normal;
                    font-weight: 600;
                    font-size: 13px;
                    line-height: 16px;
                    padding-bottom: 5px;
                }
                &--requiredField {
                    margin-left: 5px;
                }
                &--WidgetDimensionsArea {
                    display: flex !important;
                    width: auto;
                    height: fit-content;
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;
                    align-content: center;
                }
                &--SpanConfig {
                    display: flex;
                    align-items: center;
                    max-height: 32px;

                    &--LiveSpan {
                        /* width: 95%; */
                        /* padding-left: 8px; */
                        width: 100%;
                        padding: 0 8px;

                        div {
                        }
                        &__Span {
                            &.span-dropdown {
                            }

                            .span-dropdown .btn-group .open {
                            }

                            .span-dropdown {
                            }
                        }
                    }

                    &--UnfollowGlobalSpanIcon {
                        justify-content: center;
                        align-items: center;
                        align-self: center;
                        margin: 10px;
                        padding-top: 5px;
                    }
                }
            }
            &__Header {
                display: flex;
                flex-direction: 'row';
                justify-content: space-between;
                align-items: center;
                padding: 0px 3px 20px 3px;
                button {
                    padding: 5px 10px 5px 10px;
                    font-style: normal;
                    font-weight: 600;
                    font-size: 13px;
                    line-height: 16px;
                    color: #37a3ef;
                    background-color: transparent;
                    outline: none;
                    border: none;
                    transition: 0.2s;
                    i {
                        font-style: normal;
                        font-weight: normal;
                        font-size: 11px;
                        line-height: 16px;
                        padding-right: 8px;
                    }
                    &:hover {
                        background-color: #dddee1;
                    }
                    &:active {
                        background-color: #c3c3c3;
                    }
                    &:disabled {
                        color: #a0cbe9;
                        &:hover {
                            background-color: transparent;
                        }
                    }
                }
            }
        }
    }
`

const CustomPrintPreview: StylesConfig<OptionType, boolean, GroupTypeBase<OptionType>> = {
    container: styles => ({
        ...styles,
        // borderRadius: '3px !important',
        borderStyle: 'none !important',
        maxHeight: '32px !important'
    }),
    option: styles => ({
        ...styles,
        fontStyle: 'normal',
        fontWeight: 'normal',
        lineHeight: '14px ',
        fontSize: '14px '
    }),
    control: (styles, state) => ({
        ...styles,
        border: `1px solid ${state.isFocused ? 'var(--blue-500)' : 'var(--gray-500)'} !important`,
        overflow: 'hidden !important',
        borderRadius: '3px !important',
        height: 'auto ',
        maxHeight: '32px !important',
        fontFamily: 'SourceSansPro-Regular,Arial,Helvetica Neue,Helvetica,sans-serif'
    }),
    valueContainer: styles => ({
        ...styles,
        height: 'auto',
        width: 'auto',
        maxHeight: '32px !important'
    }),
    indicatorSeparator: styles => ({
        ...styles,
        display: 'flex !important',
        // // margin: 'auto',
        maxHeight: '22px ! important',
        lineHeight: '8px ! important'
    }),
    indicatorsContainer: styles => ({
        ...styles,
        display: 'flex',
        alignItems: 'center'
    }),
    placeholder: styles => ({
        ...styles,
        position: 'absolute',
        top: '50%',
        color: 'var(--gray-500)',
        textOverflow: 'ellipsis',
        whitespace: 'nowrap',
        fontFamily: 'var(--font-regular)',
        fontSize: '14px'
    }),
    menu: styles => ({
        ...styles,
        borderRadius: '0px',
        fontFamily: 'SourceSansPro-Regular,Arial,Helvetica Neue,Helvetica,sans-serif'
    }),
    clearIndicator: base => ({
        ...base,
        padding: '0px',
        scale: '90%',
        color: 'var(--gray-600)',
        '&:hover': {
            color: 'var(--gray-500)'
        }
    })
}

const SendTestButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;

    margin-top: 10px;
    max-height: 32px;
    width: 130px;
    /* border: none; */
    border-radius: 3px;
    padding-right: 8px;

    label {
        padding-top: 4px;

        i {
            padding-right: 8px;
        }
    }
`

const PrintPreviewButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;

    margin-top: 10px;
    max-height: 38px;
    width: 130px;
    /* border: none; */
    border-radius: 3px;
    padding-right: 8px;
    border: 1px solid ${({ theme }) => theme['gray-500']};
    background: ${({ theme }) => theme.white};

    label {
        padding-top: 4px;
        color: ${({ theme }) => theme['gray-800']} !important;
        cursor: pointer;
        i {
            padding-right: 8px;
            color: ${({ theme }) => theme['gray-800']} !important;
        }
    }
`

const RunPreviewButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;

    margin-top: 10px;
    max-height: 38px;
    width: 110px;
    padding-right: 8px;

    label {
        padding-top: 4px;
        cursor: pointer;
        color: ${({ theme }) => theme.white} !important;
        i {
            padding-right: 8px;
        }
    }
`

// `generalInformation--recipientsGroups__recipientsSelect`

const CustomCardEditor = styled(StyledCardEditor)<{ editMode? }>`
    border: 1px solid ${({ theme, editMode }) => (editMode ? theme['blue-500'] : theme['gray-400'])};
    ${({ theme, editMode = false }) => {
        if (editMode === true) {
            return `background: ${theme['gray-100']};`
        }
    }}
    border-radius: 3px;
    margin-top: 10px;
    background: ${({ theme }) => theme.white};

    .cardEditor {
        /* border: 1px solid red; */
    }

    .customPreviewEmailInput {
        /* border: 2px solid green; */
        border: none;

        &__header {
            /* border: 2px solid red; */
        }

        &__bottomText {
        }
    }

    .${CLASS_PREFIX}__option {
        color: ${props => props.theme['gray-800']};
        /* padding: 100px; */
    }

    .${CLASS_PREFIX}__control {
        border: 1px solid ${({ theme }) => theme['gray-500']};
        background: ${({ theme }) => theme['gray-0']};
        max-height: 32px;
        max-width: 540px;
        margin-top: 5px;
        overflow: hidden;
        display: flex;
        align-items: center;

        .${CLASS_PREFIX}__value-container {
            max-height: 32px;
            display: flex;
            align-items: center !important;
            /* border: 2px solid red; */
        }

        &--is-focused {
            max-height: 32px;
            display: flex;
            align-items: center !important;
        }

        .${CLASS_PREFIX}__placeholder {
            /* margin-top: 6px; */
            display: flex;
            align-items: center;
        }

        &--is-focused {
            border: none;

            .${CLASS_PREFIX}__placeholder {
                display: flex;
                align-items: center;
            }
        }
    }
    .${CLASS_PREFIX}__multi-value {
        border: 1px solid ${({ theme }) => theme['gray-400']};
    }
    .${CLASS_PREFIX}__multi-value__label {
        border-right: 1px solid ${({ theme }) => theme['gray-400']};
    }
    .${CLASS_PREFIX}__indicatorSeparator {
        display: none;
    }
    .${CLASS_PREFIX}__indicatorsContainer {
        display: none;
    }

    .${CLASS_PREFIX}_secondary {
        padding: 7px 15px;
        &:focus-within,
        &:focus {
            background-color: ${props => props.theme['blue-200']};
        }

        .${CLASS_PREFIX}__input {
            display: 'flex';
            justify-content: 'center';
            align-items: 'center';
            align-content: 'center';
            margin: '0px';
            padding-bottom: '-5px !important';
            height: '16px';
        }

        .${CLASS_PREFIX}__control {
            background: transparent;

            &,
            &--is-focused {
                border: none;
                box-shadow: none;
            }
            max-height: 90px;
            overflow: auto;
        }
    }
`
